/**
 * Booking actions service - cancel, modify, refund, complete operations.
 */
import type { Booking, CancellationPreview, BookingModifyData } from './bookingService';
import { apiFetch, API_URL } from './apiFetch';

export const getCancellationPreview = async (id: string): Promise<{ success: boolean; data: CancellationPreview }> => {
  return apiFetch(`/bookings/${id}/cancel-preview`);
};

export const modifyBooking = async (id: string, data: BookingModifyData): Promise<{ success: boolean; data: Booking; message: string }> => {
  return apiFetch(`/bookings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const cancelBooking = async (id: string, cancellationReason: string = ''): Promise<{ success: boolean; data: Booking }> => {
  return apiFetch(`/bookings/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ cancellationReason })
  });
};

// Cancellation request (cliente-driven, soggetta ad approvazione vendor)
export type CancellationRequestReason = 'BAD_WEATHER' | 'HEALTH' | 'EMERGENCY' | 'FORCE_MAJEURE' | 'OTHER';

export interface CancellationRequestPayload {
  reason: CancellationRequestReason;
  note?: string;
}

export const requestCancellation = async (
  bookingId: string,
  payload: CancellationRequestPayload,
): Promise<{ success: boolean; message?: string; data?: { id: string; cancellation_request_status: string } }> => {
  return apiFetch(`/bookings/${bookingId}/request-cancellation`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

// Vendor decide la richiesta del cliente
export const decideCancellationRequest = async (
  bookingId: string,
  payload: { action: 'approve' | 'reject'; responseNote?: string; refundAmount?: number; skipRefund?: boolean },
): Promise<{ success: boolean; message?: string; data?: { status: string; refundStatus?: string; amount?: number } }> => {
  return apiFetch(`/vendor/bookings/${bookingId}/cancellation-request/decide`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateBookingStatus = async (id: string, status: string): Promise<{ success: boolean; data: Booking }> => {
  return apiFetch(`/bookings/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  });
};

export const refundDeposit = async (id: string): Promise<{ success: boolean; data: Booking; message: string }> => {
  return apiFetch(`/bookings/${id}/refund-deposit`, {
    method: 'POST',
  });
};

export const downloadReceipt = async (id: string): Promise<void> => {
  // downloadReceipt needs raw fetch because it downloads a blob, not JSON
  const response = await fetch(`${API_URL}/bookings/${id}/receipt`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download receipt');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `InsulaCharter_${id}_receipt.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const markAsCompleted = async (id: string): Promise<{ success: boolean; data: Booking }> => {
  return apiFetch(`/bookings/${id}/complete`, {
    method: 'POST',
  });
};
